import React, { useState, useEffect, useCallback } from "react";
import CreateTheme from "../createTheme/createTheme";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../themeSlice";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./home.css";
import { selectAdminToken } from "../../sessionSlice";
import UpdateThemeImages from "../updateThemeImage/updateThemeImage";
import { s3ThemeAssetUrl } from "../../../utils/themeAssets";

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [themes, setThemes] = useState([]);
  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { status, data } = useSelector((state) => state.theme);

  const selectedImageUrl = s3ThemeAssetUrl(data?.themeImage);

  const fetchAllThemes = useCallback(() => {
    if (!adminToken) return;
    fetch(process.env.REACT_APP_BACKEND_URL + "/fetchAllThemes", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        test: "test",
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((json) => {
        if (json.success) {
          setThemes(Array.isArray(json.data) ? json.data : []);
        } else {
          console.error("API error:", json.message);
        }
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [adminToken]);

  // Load selected theme first (seeds missing themeImage via processThemesData),
  // then load the theme list so the right panel is not empty on first visit.
  useEffect(() => {
    if (!adminToken || isUpdateModalOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await dispatch(fetchThemeData({ isAdmin: true })).unwrap();
      } catch (err) {
        console.error("fetchThemeData error:", err);
      }
      if (!cancelled) fetchAllThemes();
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, adminToken, isUpdateModalOpen, fetchAllThemes]);

  const browseTheme = async (themeName) => {
    try {
      await dispatch(fetchThemeData({ themeId: themeName })).unwrap();
      navigate("/admin/rules");
    } catch (err) {
      Swal.fire("Error loading theme", err.message, "error");
    }
  };

  const handleSelectTheme = (themeName) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/selectTheme`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        themeName,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          console.error("SelectTheme error:", json.message);
        } else {
          Swal.fire({
            title: "Theme Selected",
            text: "",
            icon: "success",
          });
          fetchAllThemes();
          dispatch(fetchThemeData({ isAdmin: true }));
        }
      })
      .catch((err) => console.error("SelectTheme fetch error:", err));
  };

  const deleteTheme = (themeName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete the theme ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteTheme`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ themeName }),
        })
          .then((res) => res.json())
          .then((json) => {
            if (!json.success) {
              console.error("deleteTheme error:", json.message);
              Swal.fire("Error", json.message, "error");
            } else {
              Swal.fire("Deleted!", `Theme has been deleted.`, "success").then(
                () => {
                  window.location.reload();
                }
              );
            }
          })
          .catch((err) => {
            console.error("deleteTheme fetch error:", err);
            Swal.fire("Error", "Network error, please try again.", "error");
          });
      }
    });
  };

  const OpenUpdateModal = (themeName) => {
    dispatch(fetchThemeData({ themeId: themeName, isAdmin: true }));
    setIsUpdateModalOpen(true);
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  // Only block the whole page on the first load — keep last good preview while refetching.
  if ((status === "loading" || status === "idle") && !data) {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading themes...</p>
      </div>
    );
  }

  if (status === "failed" && !data) {
    return (
      <div className="load-theme-container">
        <p className="load-theme-error">❌ Error loading themes</p>
        <button
          type="button"
          className="te-create-btn"
          style={{ marginTop: 12 }}
          onClick={() => dispatch(fetchThemeData({ isAdmin: true }))}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <CreateTheme isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <UpdateThemeImages isOpen={isUpdateModalOpen} onClose={closeUpdateModal} />
      <div className="te-container">
        <div className="te-left">
          <div className="te-image-wrapper">
            {selectedImageUrl ? (
              <img
                src={selectedImageUrl}
                className="te-main-image"
                alt={data?.themeName || data?.themename || "Selected theme"}
              />
            ) : data?.themename ? (
              <div className="te-image-missing">Theme image not available</div>
            ) : (
              <>No Theme Found</>
            )}
          </div>

          <div className="te-info">
            <h2 className="te-name">{data?.themeName}</h2>
            <p className="te-desc">{data?.themeDescription}</p>
            {data?.themename ? (
              <div className="te-icons">
                <i
                  className="fa-solid fa-image te-icon"
                  title="Edit Image"
                  onClick={() => OpenUpdateModal(data.themename)}
                ></i>
                <i
                  className="fa-solid fa-gear te-icon"
                  title="Settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    browseTheme(data.themename);
                  }}
                ></i>
              </div>
            ) : null}
          </div>
        </div>

        <div className="te-right">
          <div className="te-select-header">
            <h3 className="te-select-title">Select Themes</h3>
            <button
              className="te-create-btn"
              onClick={() => setIsModalOpen(true)}
            >
              + Create Theme
            </button>
          </div>
          <div className="te-themes-list">
            {themes.map((theme) => {
              const thumbUrl = s3ThemeAssetUrl(theme.themeImage);
              return (
                <div
                  key={theme.id || theme.themename}
                  className="te-theme-item"
                  onClick={() => handleSelectTheme(theme.themename)}
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={theme.themename}
                      className="te-theme-image"
                    />
                  ) : (
                    <div className="te-theme-image te-theme-image--empty" />
                  )}

                  {theme.selected === "true" && (
                    <i
                      className="fa-solid fa-check te-selected-icon"
                      title="Selected"
                    />
                  )}

                  <div className="te-icons">
                    <i
                      className="fa-solid fa-image te-icon"
                      title="Edit Image"
                      onClick={(e) => {
                        e.stopPropagation();
                        OpenUpdateModal(theme.themename);
                      }}
                    ></i>
                    <i
                      className="fa-solid fa-gear te-icon"
                      title="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        browseTheme(theme.themename);
                      }}
                    />
                    <i
                      className="fa-solid fa-trash te-icon"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTheme(theme.themename);
                      }}
                    ></i>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
